/**
 * 회원가입 폼 상태 관리 hook
 */

import { useState, useEffect } from "react";
import { SignupFormData, RegistrationType } from "../model/signup-types";
import { validateForm } from "../utils/form-validation";

type FormMode = "signup" | "edit";

export const useSignupForm = (
  type: RegistrationType,
  mode: FormMode = "signup",
  initialData?: Partial<SignupFormData>
) => {
  const isEditMode = mode === "edit";

  const [formData, setFormData] = useState<SignupFormData>({
    provider: "local",
    user_type: initialData?.user_type || type,
    user_id: initialData?.user_id || "",
    user_phone: initialData?.user_phone || "",
    email: initialData?.email || "",
    gender: initialData?.gender,
    username: initialData?.username || "",
    password: "",
    passwordConfirm: "",
    address: initialData?.address || "",
    address_detail: initialData?.address_detail || "",
    zip_code: initialData?.zip_code || "",
    business_number: initialData?.business_number || "",
    manager_name: initialData?.manager_name || "",
    manager_phone: initialData?.manager_phone || "",
    emailVerificationCode: "",
    isEmailVerified: isEditMode ? true : initialData?.isEmailVerified || false,
    agreeToTerms: isEditMode ? true : initialData?.agreeToTerms || false,
    agreeToPrivacy: isEditMode ? true : initialData?.agreeToPrivacy || false,
    agreeToMarketing: isEditMode
      ? true
      : initialData?.agreeToMarketing || false,
    term_agreements: isEditMode ? true : initialData?.term_agreements || false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [emailSent, setEmailSent] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [openModal, setOpenModal] = useState<
    "terms" | "privacy" | "marketing" | "address" | null
  >(null);

  // 초기 데이터가 변경되면 폼 데이터 업데이트
  useEffect(() => {
    if (isEditMode && initialData) {
      setFormData((prev) => ({
        ...prev,
        user_id: initialData.user_id || prev.user_id,
        user_phone: initialData.user_phone || prev.user_phone,
        email: initialData.email || prev.email,
        gender: initialData.gender ?? prev.gender,
        username: initialData.username || prev.username,
        address: initialData.address || prev.address,
        address_detail: initialData.address_detail || prev.address_detail,
      }));
    }
  }, [initialData, isEditMode]);

  const handleChange = (
    field: keyof SignupFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // 에러 제거
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleValidate = () => {
    const newErrors = validateForm(formData, isEditMode, changePassword, type);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAllAgree = () => {
    setFormData((prev) => ({
      ...prev,
      agreeToTerms: !prev.agreeToTerms,
      agreeToPrivacy: !prev.agreeToPrivacy,
      agreeToMarketing: !prev.agreeToMarketing,
    }));
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    emailSent,
    setEmailSent,
    changePassword,
    setChangePassword,
    openModal,
    setOpenModal,
    isEditMode,
    handleChange,
    handleValidate,
    handleAllAgree,
  };
};
